/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Heart, MessageCircle, User, Sparkles, Flame, LogOut, Settings, Shield, Flag } from 'lucide-react';
import io from 'socket.io-client';
import DateBookingModal from './DateBookingModal';
import VerifyIdentity from './VerifyIdentity';
import FeedbackButton from './components/FeedbackButton';
import OnboardingShell from './narb-auth/src/onboarding/OnboardingShell.jsx';
import { getSparkOnboardingSteps } from './narb-auth/src/onboarding/steps/SparkSteps.jsx';
import AuthScreen from './AuthScreen';
import { useTheme } from './ThemeProvider';
import { shouldNotify } from './NotificationSettings';

import SubscriptionPaywall from './SubscriptionPaywall';

import AU_PLACES from './auPlaces';
import { API_URL, SPARK_URL, SOCKET_URL } from './apiConfig';

// View components
import SettingsView from './SettingsView';
import ProfileView from './ProfileView';
import DiscoverView from './DiscoverView';
import LikesView from './LikesView';
import MatchesView from './MatchesView';
import DatesView from './DatesView';
import DatePlannerView from './DatePlannerView';
import DiscoveryModeSelector from './components/DiscoveryModeSelector';
import BrowseMode from './components/BrowseMode';
import BlindMode from './components/BlindMode';
import PassedList from './components/PassedList';
import AccessibilitySettings from './components/AccessibilitySettings';
import { AccessibilityToggle, useAccessibilityStore, detectAccessibilityNeeds } from './narb-auth/src/accessibility';
import { loadAndApplyPreferences } from './utils/preferencesLoader';
import { API_BASE } from './apiConfig';

export default function DatingApp() {
  const { theme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [geoStatus, setGeoStatus] = useState('checking'); // 'checking' | 'allowed' | 'blocked'
  const [needsVerification, setNeedsVerification] = useState(false);
  const [currentView, setCurrentView] = useState('discover');
  const [discoveryMode, setDiscoveryMode] = useState('stack');
  const [showPassedList, setShowPassedList] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [matches, setMatches] = useState([]);
  const matchesRef = useRef([]);
  const [archivedMatches, setArchivedMatches] = useState([]);
  const [showArchivedMatches, setShowArchivedMatches] = useState(false);
  const [likedBy, setLikedBy] = useState([]);
  const [dateBookings, setDateBookings] = useState([]);
  const [archivedDateBookings, setArchivedDateBookings] = useState([]);
  const [showArchivedDates, setShowArchivedDates] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  
  // Touch/drag swipe gesture - use refs for smooth performance
  const touchStartRef = useRef(null);
  const touchDeltaRef = useRef(0);
  const isSwipingRef = useRef(false);
  const exploreMapRef = useRef(null);
  const exploreMapInstance = useRef(null);
  const [touchDelta, setTouchDelta] = useState(0); // Only for render updates
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [messages, setMessages] = useState({});
  const [messageInput, setMessageInput] = useState('');
  const [moderationWarning, setModerationWarning] = useState(null); // { message, strikes, isBanned }
  const [showDateModal, setShowDateModal] = useState(false);
  const [counterProposalBooking, setCounterProposalBooking] = useState(null);
  const [reportModal, setReportModal] = useState({ show: false, userId: null, userName: '', source: '' });
  const [reportReason, setReportReason] = useState('');
  const [reportMessage, setReportMessage] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [socket, setSocket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [interestSearch, setInterestSearch] = useState('');
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [activeInterestTab, setActiveInterestTab] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [showSubscriptionManage, setShowSubscriptionManage] = useState(false);
  const [skippedPaywall, setSkippedPaywall] = useState(false);
  const [discoverPhotoIndex, setDiscoverPhotoIndex] = useState(0);
  const [lastSwipe, setLastSwipe] = useState(null); // For undo functionality
  const [locationMode, setLocationMode] = useState('auto'); // 'auto' | 'postcode' | 'explore'
  const [locationFailed, setLocationFailed] = useState(false);
  const [postcodeInput, setPostcodeInput] = useState('');
  const [exploreInput, setExploreInput] = useState('');
  const [exploreSuggestions, setExploreSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [exploreLocation, setExploreLocation] = useState(null); // { lat, lng, name }
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [densityData, setDensityData] = useState([]); // user density by region
  const [densityLoading, setDensityLoading] = useState(false);
  const [expandedScores, setExpandedScores] = useState({
    interests: false,
    survey: false,
    lifestyle: false,
    mbti: false
  });
  const [locationUpdatePrompt, setLocationUpdatePrompt] = useState({
    show: false,
    currentCity: '',
    newCity: '',
    newCoordinates: [],
    distance: 0
  });

  // Profile editing state
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [showLoginImport, setShowLoginImport] = useState(false);
  const [loginImportData, setLoginImportData] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    survey: false,     // Compatibility survey
    photos: true,      // Photos section open by default
    about: false,      // Bio, occupation, education, height
    interests: false,  // Interests
    lifestyle: false,  // Kids, religion, pets, smoking, drinking, cannabis
    personality: false, // MBTI
    discovery: false   // Discovery preferences
  });
  const [expandedSettings, setExpandedSettings] = useState({
    appearance: false,
    security: false,
    verification: false,
    notifications: false,
    subscription: true,
    accessibility: false,
    preferences: false,
    about: false,
    deleteAccount: false
  });
  const [surveyData, setSurveyData] = useState({
    socialEnergy: '',
    weekend: '',
    conflict: '',
    planning: '',
    values: '',
    communication: '',
    interests: '',
    lifestyle: ''
  });
  const [profileForm, setProfileForm] = useState({
    bio: '',
    interests: [],
    occupation: '',
    education: '',
    height: '',
    photos: [],
    preferences: {
      ageMin: 18,
      ageMax: 50,
      distance: 50,
      interestedIn: []
    },
    surveyResults: {
      socialEnergy: '',
      weekend: '',
      conflict: '',
      planning: '',
      values: '',
      communication: '',
      interests: '',
      lifestyle: ''
    },
    personalityProfile: '',
    mbtiType: '',
    kids: '',
    religion: '',
    pets: ''
  });

  // Auth form state
  const currentProfile = profiles[currentIndex];

  // Helper function to calculate distance between two coordinates
  const calculateDistance = (coords1, coords2) => {
    const [lon1, lat1] = coords1;
    const [lon2, lat2] = coords2;
    
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance in km
  };

  // Check for location updates
  const checkLocationUpdate = useCallback(async () => {
    // Skip if already checked recently (within last hour)
    const lastCheck = localStorage.getItem('lastLocationCheck');
    if (lastCheck && Date.now() - parseInt(lastCheck) < 3600000) {
      return;
    }

    // Skip if no profile or no saved location
    if (!profile || !profile.location || !profile.location.coordinates) {
      return;
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const currentLat = position.coords.latitude;
          const currentLng = position.coords.longitude;
          
          // Get saved location
          const savedCoords = profile.location.coordinates;
          
          // Calculate distance
          const distance = calculateDistance(
            [savedCoords[0], savedCoords[1]], 
            [currentLng, currentLat]
          );
          
          console.log(`📍 Distance from saved location: ${Math.round(distance)} km`);
          
          // If more than 50km away, prompt user
          if (distance > 50) {
            try {
              // Reverse geocode to get city name
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${currentLat}&lon=${currentLng}&zoom=10`,
                { headers: { 'User-Agent': 'SparkDatingApp/1.0' } }
              );
              const geoData = await response.json();
              const newCity = geoData.address?.city || 
                              geoData.address?.town || 
                              geoData.address?.suburb ||
                              geoData.address?.village || 
                              'this location';
              
              console.log(`📍 New location detected: ${newCity} (${Math.round(distance)} km away)`);
              
              // Show prompt
              setLocationUpdatePrompt({
                show: true,
                currentCity: profile.location?.city || 'your saved location',
                newCity: newCity,
                newCoordinates: [currentLng, currentLat],
                distance: Math.round(distance)
              });
            } catch (error) {
              console.error('Error getting city name:', error);
            }
          }
          
          localStorage.setItem('lastLocationCheck', Date.now().toString());
        },
        (error) => {
          console.log('📍 Location permission denied or unavailable:', error?.message);
          setLocationFailed(true);
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    }
  }, [profile]);

  // Look up Australian postcode → coordinates
  const handlePostcodeLookup = async () => {
    const postcode = postcodeInput.trim();
    if (!postcode || postcode.length !== 4 || !/^\d{4}$/.test(postcode)) {
      setLocationError('Please enter a valid 4-digit Australian postcode');
      return;
    }
    
    setLocationLoading(true);
    setLocationError('');
    
    try {
      // Use Nominatim to geocode the Australian postcode
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&postalcode=${postcode}&country=Australia&limit=1`,
        { headers: { 'User-Agent': 'SparkDatingApp/1.0' } }
      );
      const results = await response.json();
      
      if (results.length === 0) {
        setLocationError('Postcode not found. Please check and try again.');
        setLocationLoading(false);
        return;
      }
      
      const { lat, lon, display_name } = results[0];
      const cityName = display_name.split(',')[0].trim();
      
      // Update profile location with the postcode coordinates
      const token = localStorage.getItem('accessToken');
      await fetch(`${SPARK_URL}/profiles/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          location: {
            type: 'Point',
            coordinates: [parseFloat(lon), parseFloat(lat)],
            city: cityName,
            state: '',
            country: 'Australia'
          }
        })
      });
      
      // Reload profile and discover
      await fetchCurrentUser(token, true);
      await loadDiscoverProfiles(token);
      setLocationMode('auto');
      setLocationFailed(false);
      setPostcodeInput('');
      console.log(`✅ Location set via postcode ${postcode}: ${cityName}`);
    } catch (err) {
      console.error('Postcode lookup error:', err);
      setLocationError('Failed to look up postcode. Please try again.');
    } finally {
      setLocationLoading(false);
    }
  };

  // Debounced autocomplete for city/suburb search
  const handleExploreInputChange = (value) => {
    setExploreInput(value);
    
    const trimmed = value.trim().toLowerCase();
    
    if (trimmed.length < 2) {
      setExploreSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    // Instant local filtering — no API call needed
    const matches = AU_PLACES
      .map(([name, state, lat, lng], idx) => ({
        name, state, lat, lng,
        display: `${name}, ${state}`,
        // Prioritise: starts-with > word-boundary > contains, then by array order (population)
        score: name.toLowerCase().startsWith(trimmed) ? 0 :
               name.toLowerCase().includes(trimmed) ? 1 :
               `${name} ${state}`.toLowerCase().includes(trimmed) ? 2 : 3,
        idx
      }))
      .filter(m => m.score < 3)
      .sort((a, b) => a.score - b.score || a.idx - b.idx)
      .slice(0, 5);
    
    setExploreSuggestions(matches);
    setShowSuggestions(trimmed.length >= 2);
  };

  // Select a suggestion
  const handleSelectSuggestion = async (suggestion) => {
    setExploreInput(suggestion.name);
    setShowSuggestions(false);
    setExploreSuggestions([]);
    
    setExploreLocation({ lat: suggestion.lat, lng: suggestion.lng, name: suggestion.name });
    setLocationMode('explore');
    await loadDiscoverProfiles(null, { lat: suggestion.lat, lng: suggestion.lng });
    setExploreInput('');
    console.log(`🔍 Exploring profiles near ${suggestion.name}`);
  };

  // Explore a different location (doesn't update profile, just changes discover results)
  const handleExploreLookup = async () => {
    const query = exploreInput.trim();
    if (!query) {
      setLocationError('Please enter a city or suburb name');
      return;
    }
    
    setShowSuggestions(false);
    setExploreSuggestions([]);
    setLocationLoading(true);
    setLocationError('');
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)},+Australia&limit=1`,
        { headers: { 'User-Agent': 'SparkDatingApp/1.0' } }
      );
      const results = await response.json();
      
      if (results.length === 0) {
        setLocationError('Location not found. Try a different city or suburb name.');
        setLocationLoading(false);
        return;
      }
      
      const { lat, lon, display_name } = results[0];
      const cityName = display_name.split(',')[0].trim();
      
      setExploreLocation({ lat: parseFloat(lat), lng: parseFloat(lon), name: cityName });
      
      // Load profiles for that location (without updating profile)
      await loadDiscoverProfiles(null, { lat: parseFloat(lat), lng: parseFloat(lon) });
      setLocationMode('explore');
      setExploreInput('');
      console.log(`🔍 Exploring profiles near ${cityName}`);
    } catch (err) {
      console.error('Explore lookup error:', err);
      setLocationError('Failed to look up location. Please try again.');
    } finally {
      setLocationLoading(false);
    }
  };

  // Reset explore mode and go back to normal discover
  const handleResetExplore = async () => {
    setExploreLocation(null);
    setLocationMode('auto');
    await loadDiscoverProfiles();
  };

  // Fetch user density data for the explore map
  const fetchDensityData = async () => {
    setDensityLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/profiles/density`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDensityData(data.data);
      }
    } catch (err) {
      console.error('Error fetching density data:', err);
    } finally {
      setDensityLoading(false);
    }
  };

  // When user taps a region on the map
  const handleMapExplore = async (region) => {
    setExploreLocation({ lat: region.lat, lng: region.lng, name: region.city });
    setLocationMode('explore');
    await loadDiscoverProfiles(null, { lat: region.lat, lng: region.lng });
  };

  // Load Leaflet CSS/JS from CDN once
  const loadLeaflet = useCallback(() => {
    return new Promise((resolve) => {
      if (window.L) { resolve(window.L); return; }
      
      // Load CSS
      if (!document.getElementById('leaflet-css')) {
        const css = document.createElement('link');
        css.id = 'leaflet-css';
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(css);
      }
      
      // Load JS
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => resolve(window.L);
        document.head.appendChild(script);
      } else {
        // Script tag exists but might still be loading
        const check = setInterval(() => {
          if (window.L) { clearInterval(check); resolve(window.L); }
        }, 100);
      }
    });
  }, []);

  // Initialize/update Leaflet map when explore panel opens and density data arrives
  useEffect(() => {
    if (locationMode !== 'explore-input' || exploreLocation) return;
    
    let cancelled = false;
    
    const initMap = async () => {
      const L = await loadLeaflet();
      if (cancelled || !exploreMapRef.current) return;
      
      // Destroy previous map if it exists
      if (exploreMapInstance.current) {
        exploreMapInstance.current.remove();
        exploreMapInstance.current = null;
      }
      
      const map = L.map(exploreMapRef.current, {
        center: [-25.5, 134],
        zoom: 4,
        minZoom: 3,
        maxZoom: 12,
        zoomControl: true,
        attributionControl: false
      });
      
      // Use CartoDB dark/light tiles based on surface colour
      const surface = getComputedStyle(document.documentElement).getPropertyValue('--surface').trim();
      const isDark = surface && (surface.startsWith('#0') || surface.startsWith('#1') || surface === '#000000');
      
      L.tileLayer(isDark 
        ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
        : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', 
      {
        maxZoom: 19,
        subdomains: 'abcd'
      }).addTo(map);
      
      exploreMapInstance.current = map;
      
      // Add density markers
      if (densityData.length > 0) {
        const maxCount = Math.max(...densityData.map(d => d.count), 1);
        
        densityData.forEach(region => {
          const intensity = region.count / maxCount;
          const radius = Math.max(12, Math.min(40, 12 + intensity * 28));
          
          // Colour: blue (few) → orange (medium) → red (many)
          const hue = intensity > 0.6 ? 0 : intensity > 0.3 ? 30 : 210;
          const sat = 70 + intensity * 30;
          const light = 55 - intensity * 10;
          const colour = `hsl(${hue}, ${sat}%, ${light}%)`;
          
          const marker = L.circleMarker([region.lat, region.lng], {
            radius: radius,
            fillColor: colour,
            fillOpacity: 0.7,
            color: colour,
            weight: 2,
            opacity: 0.9
          }).addTo(map);
          
          // Tooltip
          marker.bindTooltip(
            `<div style="text-align:center;font-family:inherit">
              <strong>${region.city}</strong><br/>
              <span style="font-size:13px">${region.count} ${region.count === 1 ? 'person' : 'people'}</span>
            </div>`,
            { direction: 'top', offset: [0, -radius] }
          );
          
          // Click to explore
          marker.on('click', () => handleMapExplore(region));
        });
      }
      
      // "You are here" marker
      if (profile?.location?.coordinates) {
        const myLng = profile.location.coordinates[0];
        const myLat = profile.location.coordinates[1];
        const primaryColour = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim() || '#6366f1';
        
        L.circleMarker([myLat, myLng], {
          radius: 7,
          fillColor: primaryColour,
          fillOpacity: 1,
          color: 'white',
          weight: 3
        }).addTo(map).bindTooltip(
          `<strong>${profile.location.city || 'You'}</strong><br/><span style="font-size:12px">Your location</span>`,
          { direction: 'top', offset: [0, -10] }
        );
      }
      
      // Force a resize after a short delay (fixes rendering in hidden containers)
      setTimeout(() => { if (map) map.invalidateSize(); }, 200);
    };
    
    initMap();
    
    return () => { 
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationMode, exploreLocation, densityData, profile, loadLeaflet]);

  // Cleanup map when leaving explore mode
  useEffect(() => {
    if (locationMode !== 'explore-input') {
      if (exploreMapInstance.current) {
        exploreMapInstance.current.remove();
        exploreMapInstance.current = null;
      }
    }
  }, [locationMode]);

  // Keep matchesRef in sync so socket callbacks always have current matches
  useEffect(() => {
    matchesRef.current = matches;
  }, [matches]);

  // Geo check — Australia only
  useEffect(() => {
    const checkGeo = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/');
        const data = await res.json();
        if (data.country_code && data.country_code !== 'AU') {
          setGeoStatus('blocked');
        } else {
          setGeoStatus('allowed');
        }
      } catch {
        // Fail-open — if lookup fails, allow the user through
        setGeoStatus('allowed');
      }
    };
    checkGeo();
  }, []);

  // ── Accessibility — Layer 1 (detect at app load) ──────────────────────────
  // Runs detector to check three signals (URL override, screen reader, saved
  // preference) and initialises the accessibility store. Safe to run before
  // auth — detector handles missing token gracefully. Toggle (rendered below)
  // is the reliable fallback regardless of detection result.
  const accessibilityInitialise = useAccessibilityStore(s => s.initialise);
  const accessibilityHasInitialised = useAccessibilityStore(s => s.hasInitialised);
  useEffect(() => {
    if (accessibilityHasInitialised) return;
    const token = localStorage.getItem('accessToken');
    detectAccessibilityNeeds({ token, apiBase: API_URL }).then(detectorResult => {
      accessibilityInitialise({ token, apiBase: API_URL, detectorResult });
    });
  }, [isAuthenticated, accessibilityHasInitialised, accessibilityInitialise]);


  // Reset discover photo index when we move to a new profile
  useEffect(() => {
    setDiscoverPhotoIndex(0);
  }, [currentIndex]);

  // Check location when discover view loads
  useEffect(() => {
    if (currentView === 'discover' && isAuthenticated && profile) {
      checkLocationUpdate();
    }
  }, [currentView, isAuthenticated, profile, checkLocationUpdate]);

  // Common interests list

  // Flatten all interests for backward compatibility
  // Interests data is used directly from interestsData object

  // Initialize socket connection when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const token = localStorage.getItem('accessToken');
      const newSocket = io(SOCKET_URL, {
        auth: { token }
      });

      newSocket.on('connect', () => {
        console.log('Connected to socket server');
      });

      newSocket.on('new-message', (data) => {
        setMessages(prev => ({
          ...prev,
          [data.matchId]: [...(prev[data.matchId] || []), data.message]
        }));
      });

      newSocket.on('new-like', (data) => {
        // Add the new liker to the likedBy list
        setLikedBy(prev => [data.user, ...prev]);
        if (shouldNotify('likes')) {
          console.log(`💜 ${data.user.firstName} liked you!`);
        }
      });

      newSocket.on('new-match', (data) => {
        setMatches(prev => [...prev, data]);
        // Remove the matched user from likedBy if they were there
        setLikedBy(prev => prev.filter(p => p.userId?.toString() !== data.user?.userId?.toString()));
        if (shouldNotify('matches')) {
          alert(`New match with ${data.user.firstName}! 🎉`);
        }
      });

      // Date proposal events
      newSocket.on('date-proposed', (data) => {
        console.log('Date proposed:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateProposals')) {
          alert(`📅 ${data.dateBooking ? 'New date proposal!' : 'You have a new date proposal!'}`);
        }
      });

      newSocket.on('date-response', (data) => {
        console.log('Date response:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateUpdates')) {
          const status = data.status === 'accepted' ? 'accepted ✅' : 'declined ❌';
          alert(`Your date proposal was ${status}`);
        }
      });

      newSocket.on('date-cancelled', (data) => {
        console.log('Date cancelled:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateUpdates')) {
          alert('🚫 A date was cancelled');
        }
      });

      newSocket.on('date-accepted', (data) => {
        console.log('Date accepted:', data);
        loadAllDateBookings(token);
        if (data.bookingStatus === 'needs_booking') {
          alert(`🎉 ${data.otherUserName || 'Your match'} accepted your date!\n\n📞 Don't forget to book your table at ${data.venue || 'the venue'}!`);
        }
      });

      newSocket.on('booking-confirmed', (data) => {
        console.log('Booking confirmed:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateUpdates')) {
          alert(`✅ Booking confirmed at ${data.venue}! ${data.confirmationNote || ''}`);
        }
      });

      newSocket.on('date-changes-suggested', (data) => {
        console.log('Changes suggested:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateUpdates')) {
          alert(`💬 ${data.suggestedBy} is keen for the date but has some changes to suggest!`);
        }
      });

      newSocket.on('date-changes-accepted', (data) => {
        console.log('Changes accepted:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateUpdates')) {
          alert(`✅ Your changes were accepted! The date at ${data.venue} is confirmed.`);
        }
      });

      newSocket.on('date-changes-declined', (data) => {
        console.log('Changes declined:', data);
        loadAllDateBookings(token);
        if (shouldNotify('dateUpdates')) {
          alert(`The original plan was kept. You can still accept or decline the date.`);
        }
      });

      setSocket(newSocket);

      return () => newSocket.close();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isAuthenticated, user]);

  // Check if user is already logged in + handle Stripe return
  useEffect(() => {

    // ── Cross-app SSO handoff — check BEFORE showing auth screen ──────────────
    const _handoffParams = new URLSearchParams(window.location.search);
    const _handoffToken = _handoffParams.get('narb_handoff');
    if (_handoffToken) {
      const _cleanUrl = new URL(window.location.href);
      _cleanUrl.searchParams.delete('narb_handoff');
      window.history.replaceState({}, '', _cleanUrl.toString());
      fetch('${API_URL}/api/auth/cross-app/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: _handoffToken }),
      }).then(r => r.json()).then(d => {
        if (d.success && d.data?.tokens) {
          localStorage.setItem('accessToken', d.data.tokens.accessToken);
          if (d.data.tokens.refreshToken) localStorage.setItem('accessToken_refresh', d.data.tokens.refreshToken);
          
          fetchCurrentUser(d.data.tokens.accessToken); checkAdminStatus(d.data.tokens.accessToken);
        }
      }).catch(() => {});
    }
    const token = localStorage.getItem('accessToken');
    if (token) {
      fetchCurrentUser(token);
      checkAdminStatus(token);
    }

    // Handle return from Stripe Checkout
    const urlParams = new URLSearchParams(window.location.search);
    const subscriptionParam = urlParams.get('subscription');
    if (subscriptionParam === 'success') {
      // Remove query params from URL
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh subscription after a short delay (Stripe webhook may take a moment)
      if (token) {
        setTimeout(async () => {
          try {
            const response = await fetch(`${API_URL}/subscription/status`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
              setSubscription(data.data.subscription);
            }
          } catch (err) {
            console.error('Failed to refresh subscription after checkout:', err);
          }
        }, 2000);
      }
    } else if (subscriptionParam === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Check if user is admin
  const checkAdminStatus = async (token) => {
    try {
      const response = await fetch(`${API_URL}/admin/check-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success && data.data.isAdmin) {
        setIsAdmin(true);
      }
    } catch (err) {
      console.log('Not an admin');
    }
  };

  // Fetch current user
  const fetchCurrentUser = async (token, skipVerificationCheck = false) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.data.user);
        
        // If no Spark profile exists yet, initialize one
        let profileData = data.data.profile;
        if (!profileData) {
          try {
            const sparkRes = await fetch(`${SPARK_URL}/onboarding/init`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            const sparkData = await sparkRes.json();
            if (sparkData.success) {
              profileData = sparkData.data.profile;
              console.log('⚡ Spark profile auto-initialized on login');
            }
          } catch (sparkErr) {
            console.error('⚡ Spark onboarding error:', sparkErr);
          }
        }
        
        setProfile(profileData);
        // Capture subscription data
        if (data.data.subscription) {
          setSubscription(data.data.subscription);
        }
        // New user detection — no bio AND no photos = needs onboarding
        const isNewUser = !profileData?.bio && (!profileData?.photos || profileData.photos.length === 0) && (!profileData?.interests || profileData.interests.length === 0);
        setNeedsOnboarding(isNewUser);
		console.log('🔍 FULL PROFILE DATA:', profileData);
console.log('🔍 Profile bio:', profileData?.bio);
console.log('🔍 Profile interests:', profileData?.interests);
console.log('🔍 Profile occupation:', profileData?.occupation);
console.log('🔍 Profile education:', profileData?.education);
        setIsAuthenticated(true);
        loadDiscoveryMode();
        loadAndApplyPreferences(localStorage.getItem('accessToken'));
		// Only check verification if not skipping
		if (!skipVerificationCheck && !data.data.user.isIdentityVerified) {
		setNeedsVerification(true);
		return;
		}
        
        // Set profile form with existing data
        if (profileData) {
          console.log('📝 Loading profile into form:', profileData);
          
          const formData = {
            bio: profileData.bio || '',
            interests: profileData.interests || [],
            occupation: profileData.occupation || '',
            education: profileData.education || '',
            height: profileData.height?.toString() || '',
            photos: profileData.photos || [],
            preferences: {
              ageMin: profileData.preferences?.minAge || 18,
              ageMax: profileData.preferences?.maxAge || 50,
              distance: profileData.preferences?.maxDistance || 50,
              interestedIn: profileData.preferences?.interestedIn || []
            },
            surveyResults: profileData.surveyResults || {
              socialEnergy: '',
              weekend: '',
              conflict: '',
              planning: '',
              values: '',
              communication: '',
              interests: '',
              lifestyle: ''
            },
            personalityProfile: profileData.personalityProfile || ''
          };
          
          // Load MBTI type from profile
          formData.mbtiType = profileData.mbtiType || '';
          formData.kids = profileData.kids || '';
          formData.religion = profileData.religion || '';
          formData.pets = profileData.pets || '';
          // Read from preferences.lifestyle (new location) or fall back to top level (old data)
          formData.smoking = profileData.preferences?.lifestyle?.smoking || profileData.smoking || '';
          formData.drinking = profileData.preferences?.lifestyle?.drinking || profileData.drinking || '';
          formData.marijuana = profileData.preferences?.lifestyle?.cannabis || profileData.marijuana || '';
          
          console.log('📋 Form data being set:', formData);
          setProfileForm(formData);
          
          // Also populate surveyData state if survey results exist
          if (profileData.surveyResults) {
            setSurveyData({
              socialEnergy: profileData.surveyResults.socialEnergy || '',
              weekend: profileData.surveyResults.weekend || '',
              conflict: profileData.surveyResults.conflict || '',
              planning: profileData.surveyResults.planning || '',
              values: profileData.surveyResults.values || '',
              communication: profileData.surveyResults.communication || '',
              interests: profileData.surveyResults.interests || '',
              lifestyle: profileData.surveyResults.lifestyle || ''
            });
            console.log('📊 Survey data loaded:', profileData.surveyResults);
          }
        }
        
        loadDiscoverProfiles(token);
        loadMatches(token);
        loadLikesReceived(token);
        
        // Check for cross-app import opportunity (if profile has empty fields)
        if (profileData && !skipVerificationCheck) {
          checkSparkLoginImport(token);
        }
        
        // Warm the location cache in background so profile saves are instant
        if ('geolocation' in navigator && !window._cachedSparkLocation) {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              const lat = pos.coords.latitude;
              const lng = pos.coords.longitude;
              try {
                const r = await fetch(
                  `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`,
                  { headers: { 'User-Agent': 'SparkDatingApp/1.0' } }
                );
                if (r.ok) {
                  const d = await r.json();
                  window._cachedSparkLocation = {
                    type: 'Point',
                    coordinates: [lng, lat],
                    city: d.address?.city || d.address?.town || d.address?.suburb || 'Unknown City',
                    state: d.address?.state || '',
                    country: d.address?.country || 'Unknown'
                  };
                  console.log('📍 Location cached:', window._cachedSparkLocation.city);
                }
              } catch (e) { /* silent */ }
            },
            () => {},
            { timeout: 5000, maximumAge: 300000, enableHighAccuracy: false }
          );
        }
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  // Cross-app import check on login
  const checkSparkLoginImport = async (token) => {
    try {
      const promptRes = await fetch(`${API_URL}/cross-app/should-prompt?currentApp=spark`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const promptData = await promptRes.json();
      if (promptData.success && promptData.data?.shouldPrompt) {
        const importRes = await fetch(`${API_URL}/cross-app/available-imports?currentApp=spark`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const importData = await importRes.json();
        if (importData.success && importData.data?.hasImports) {
          setLoginImportData(importData.data.imports);
          setShowLoginImport(true);
        }
      }
    } catch (e) { console.log('Login import check skipped:', e); }
  };
  
  const handleVerificationComplete = async () => {
    const token = localStorage.getItem('accessToken');
    
    try {
      const verifyResponse = await fetch(`${API_URL}/auth/verify-identity`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const verifyData = await verifyResponse.json();
      console.log('✅ Verification API response:', verifyData);
    } catch (err) {
      console.error('❌ Failed to call verify-identity endpoint:', err);
    }

    setNeedsVerification(false);
    setUser(prev => prev ? { ...prev, isIdentityVerified: true } : prev);
    
    if (token) {
      await fetchCurrentUser(token, true);
      loadDiscoverProfiles(token);
      loadMatches(token);
      loadLikesReceived(token);
      checkAdminStatus(token);
    }
  };
  
  // handleVerificationSkip REMOVED — verification is mandatory.
  // No unverified users. Period. Safety is our #1 concern.

  const handleOnboardingComplete = async (result) => {
    console.log('✅ Onboarding completed!', result);
    setNeedsOnboarding(false);
    
    if (result?.verified) {
      console.log('✅ User verified during onboarding - updating state');
      setUser(prev => prev ? { ...prev, isIdentityVerified: true } : prev);
    }
    
    const token = localStorage.getItem('accessToken');
    if (token) {
      await fetchCurrentUser(token, true);
      loadDiscoverProfiles(token);
      loadMatches(token);
      loadLikesReceived(token);
      checkAdminStatus(token);
    }
    
    setCurrentView('discover');
  };

// Handle forgot password

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setIsAuthenticated(false);
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setShowSettings(false);
    setSubscription(null);
    setShowSubscriptionManage(false);
    setProfiles([]);
    setMatches([]);
    setLikedBy([]);
    // Reset profile form to prevent previous user's data from showing
    setProfileForm({
      bio: '',
      interests: [],
      occupation: '',
      education: '',
      height: '',
      photos: [],
      preferences: {
        ageMin: 18,
        ageMax: 50,
        distance: 50,
        interestedIn: []
      },
      surveyResults: {
        socialEnergy: '',
        weekend: '',
        conflict: '',
        planning: '',
        values: '',
        communication: '',
        interests: '',
        lifestyle: ''
      }
    });
    // Also reset surveyData state
    setSurveyData({
      socialEnergy: '',
      weekend: '',
      conflict: '',
      planning: '',
      values: '',
      communication: '',
      interests: '',
      lifestyle: ''
    });
    if (socket) socket.close();
  };

  // Handle account deletion

  // Load discover profiles
  const loadDiscoverProfiles = async (token, overrideLocation) => {
    try {
      const accessToken = token || localStorage.getItem('accessToken');
      let url = `${SPARK_URL}/profiles/discover?limit=20`;
      
      // If override location provided (from postcode or explore), add to query
      if (overrideLocation && overrideLocation.lat && overrideLocation.lng) {
        url += `&lat=${overrideLocation.lat}&lng=${overrideLocation.lng}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setProfiles(data.data);
        setCurrentIndex(0);
      }
    } catch (err) {
      console.error('Error loading profiles:', err);
    }
  };

// Load matches
const loadMatches = async (token) => {
  try {
    const accessToken = token || localStorage.getItem('accessToken');
    
    // Load active matches
    const activeResponse = await fetch(`${SPARK_URL}/matches`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const activeData = await activeResponse.json();
    if (activeData.success) {
      console.log(`✅ Loaded ${activeData.data.length} active matches`);
      setMatches(activeData.data);

      // ?match=<id>: Maia's "Open on Spark" link (25 Oct 2026) lands on that thread.
      const wanted = new URLSearchParams(window.location.search).get('match');
      if (wanted) {
        const hit = activeData.data.find((m) => String(m.matchId) === wanted);
        window.history.replaceState({}, '', window.location.pathname);
        if (hit) { setCurrentView('matches'); setSelectedMatch(hit); loadMessages(hit.matchId); }
      }
      
      // Load date bookings after matches are loaded
      if (activeData.data.length > 0) {
        console.log('🔄 Auto-loading date bookings with', activeData.data.length, 'matches...');
        setTimeout(() => loadAllDateBookings(accessToken, activeData.data), 500);
      } else {
        console.log('ℹ️ No matches yet - no dates to load');
        setDateBookings([]);
      }
    }

    // Load archived matches
    const archivedResponse = await fetch(`${SPARK_URL}/matches?archived=true`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const archivedData = await archivedResponse.json();
    if (archivedData.success) {
      console.log(`✅ Loaded ${archivedData.data.length} archived matches`);
      setArchivedMatches(archivedData.data);
    }
  } catch (err) {
    console.error('Error loading matches:', err);
  }
};

  // Load likes received
  const loadLikesReceived = async (token) => {
    try {
      const accessToken = token || localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/swipes/received`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setLikedBy(data.data);
      }
    } catch (err) {
      console.error('Error loading likes:', err);
    }
  };

// Load all date bookings
const loadAllDateBookings = async (token, matchesArray = null) => {
  try {
    const accessToken = token || localStorage.getItem('accessToken');
    
    // Use provided matches or fall back to ref (always current) then state
    const matchesToUse = matchesArray || matchesRef.current || matches;
    
    console.log('🔍 Loading date bookings...');
    console.log('📊 Matches to check:', matchesToUse.length);
    
    // If no matches provided and state is empty, wait and try again (max 5 attempts)
    if (matchesToUse.length === 0) {
      console.log('⏳ No matches found - either not loaded yet or you have no matches');
      
      if (window.dateBookingLoadAttempts === undefined) {
        window.dateBookingLoadAttempts = 0;
      }
      
      window.dateBookingLoadAttempts++;
      
      if (window.dateBookingLoadAttempts < 5) {
        console.log(`⏳ Attempt ${window.dateBookingLoadAttempts}/5 - Waiting for matches...`);
        setTimeout(() => loadAllDateBookings(accessToken, matchesArray), 1000);
        return;
      } else {
        console.log('⚠️ No matches loaded after 5 attempts');
        window.dateBookingLoadAttempts = 0;
        setDateBookings([]);
        setArchivedDateBookings([]);
        return;
      }
    }
    
    // Reset attempt counter
    window.dateBookingLoadAttempts = 0;
    
    // Load both active and archived in parallel for each match
    const activeBookings = [];
    const archivedBookings = [];
    
    for (const match of matchesToUse) {
      try {
        console.log(`🔍 Loading dates for match ${match.matchId}...`);
        
        // Fetch active and archived in parallel
        const [activeResponse, archivedResponse] = await Promise.all([
          fetch(`${SPARK_URL}/dates/matches/${match.matchId}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` }
          }),
          fetch(`${SPARK_URL}/dates/matches/${match.matchId}?archived=true`, {
            headers: { 'Authorization': `Bearer ${accessToken}` }
          })
        ]);
        
        const [activeData, archivedData] = await Promise.all([
          activeResponse.json(),
          archivedResponse.json()
        ]);
        
        if (activeData.success && activeData.data.length > 0) {
          activeBookings.push(...activeData.data.map(booking => ({
            ...booking,
            matchUser: match.user
          })));
        }
        
        if (archivedData.success && archivedData.data.length > 0) {
          archivedBookings.push(...archivedData.data.map(booking => ({
            ...booking,
            matchUser: match.user
          })));
        }
      } catch (err) {
        console.error(`❌ Error loading dates for match ${match.matchId}:`, err);
      }
    }
    
    console.log(`✅ Loaded ${activeBookings.length} active and ${archivedBookings.length} archived date bookings`);
    setDateBookings(activeBookings);
    setArchivedDateBookings(archivedBookings);
  } catch (err) {
    console.error('❌ Error loading date bookings:', err);
  }
};

  // ── Discovery mode ──────────────────────────────────────────
  const loadDiscoveryMode = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${SPARK_URL}/discovery/mode`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.data.mode) setDiscoveryMode(data.data.mode);
    } catch (e) { /* default to stack */ }
  };

  const changeDiscoveryMode = async (mode) => {
    setDiscoveryMode(mode);
    setShowPassedList(false);
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${SPARK_URL}/discovery/mode`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
    } catch (e) { /* saved locally at least */ }
  };

  const handlePassProfile = async (profileIndex) => {
    const p = profiles[profileIndex];
    if (!p) return;
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${SPARK_URL}/discovery/pass/${p.userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (e) { /* non-critical */ }
    // Also fire existing left swipe to remove from deck
    handleSwipe('left');
  };

  const handleMaybeProfile = async (profileIndex) => {
    const p = profiles[profileIndex];
    if (!p) return;
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${SPARK_URL}/discovery/maybe/${p.userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (e) { /* non-critical */ }
    // Move to next profile
    handleSwipe('left');
  };

  // Handle swipe
  const handleSwipe = async (direction) => {
    if (!currentProfile) return;

    // Reset any drag state
    touchStartRef.current = null;
    touchDeltaRef.current = 0;
    isSwipingRef.current = false;
    setTouchDelta(0);

    // Immediately move to next profile to prevent double-swiping
    const profileToSwipeOn = currentProfile;
    const previousIndex = currentIndex; // Save for undo
    const nextIndex = currentIndex + 1;
    
    // Save last swipe for undo
    setLastSwipe({
      profile: profileToSwipeOn,
      direction,
      index: previousIndex,
      toUserId: profileToSwipeOn.userId || profileToSwipeOn._id
    });
    
    // Move to next profile immediately (optimistic update)
    if (nextIndex < profiles.length) {
      setCurrentIndex(nextIndex);
    } else {
      // Load more profiles if we're at the end
      loadDiscoverProfiles();
    }

    setSwipeDirection(direction);
    const action = direction === 'right' ? 'like' : 'pass';

    try {
      const token = localStorage.getItem('accessToken');
      
      // Log for debugging
      console.log('Swiping on:', {
        toUserId: profileToSwipeOn.userId || profileToSwipeOn._id,
        action,
        profile: profileToSwipeOn
      });

      const response = await fetch(`${SPARK_URL}/swipes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toUserId: profileToSwipeOn.userId || profileToSwipeOn._id,
          action
        })
      });

      const data = await response.json();
      console.log('Swipe response:', data);

      if (data.success && data.data.match) {
        setMatches([...matches, {
          matchId: data.data.match.id,
          user: profileToSwipeOn
        }]);
        if (shouldNotify('matches')) {
          alert(`It's a match with ${profileToSwipeOn.firstName}! 🎉`);
        }
      }

      // Clear swipe animation after a moment
      setTimeout(() => {
        setSwipeDirection(null);
      }, 300);
    } catch (err) {
      console.error('Error swiping:', err);
      setSwipeDirection(null);
      // If swipe failed, go back to previous profile
      if (currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      }
    }
  };

  // Undo last swipe
  const undoLastSwipe = async () => {
    if (!lastSwipe) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Delete the swipe from backend
      const response = await fetch(`${SPARK_URL}/swipes/${lastSwipe.toUserId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Go back to that profile
        setCurrentIndex(lastSwipe.index);
        setLastSwipe(null);
        setDiscoverPhotoIndex(0);
        console.log('Swipe undone successfully');
      } else {
        console.error('Failed to undo swipe on backend');
        // Still go back in UI even if backend fails
        setCurrentIndex(lastSwipe.index);
        setLastSwipe(null);
        setDiscoverPhotoIndex(0);
      }
    } catch (err) {
      console.error('Undo failed:', err);
      // Even if request fails, still go back in UI
      setCurrentIndex(lastSwipe.index);
      setLastSwipe(null);
      setDiscoverPhotoIndex(0);
    }
  };

  // Touch/drag swipe gesture handlers
  const handleTouchStart = (e) => {
    if (!user?.isIdentityVerified) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    touchStartRef.current = clientX;
    touchDeltaRef.current = 0;
    isSwipingRef.current = true;
    setTouchDelta(0);
  };

  const handleTouchMove = (e) => {
    if (!isSwipingRef.current || touchStartRef.current === null) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const delta = clientX - touchStartRef.current;
    touchDeltaRef.current = delta;
    setTouchDelta(delta); // Trigger re-render for visual feedback
  };

  const handleTouchEnd = () => {
    if (!isSwipingRef.current) return;
    const delta = touchDeltaRef.current;
    const threshold = 100;
    
    // Reset gesture state first
    touchStartRef.current = null;
    isSwipingRef.current = false;
    
    if (delta > threshold) {
      handleSwipe('right');
    } else if (delta < -threshold) {
      handleSwipe('left');
    }
    
    // Reset visual
    touchDeltaRef.current = 0;
    setTouchDelta(0);
  };

  // Handle like back
  const handleLikeBack = async (likeData) => {
    try {
      const profile = likeData.user || likeData;
      const targetUserId = profile.userId || profile._id;
      console.log('🔍 handleLikeBack targetUserId:', targetUserId);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/swipes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toUserId: targetUserId,
          action: 'like'
        })
      });

      const data = await response.json();

      if (data.success && data.data.match) {
        setLikedBy(likedBy.filter(p => {
          const pUser = p.user || p;
          return (pUser.userId || pUser._id) !== targetUserId;
        }));
        setMatches([...matches, {
          matchId: data.data.match.id,
          user: profile
        }]);
      }
    } catch (err) {
      console.error('Error liking back:', err);
    }
  };

  // Block a user
  const blockUser = async (userId, source = 'discover') => {
    if (!window.confirm('Are you sure you want to block this user? They will be removed from your matches and you won\'t see each other again.')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/block/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });

      const data = await response.json();

      if (data.success) {
        // Remove from appropriate list based on source
        if (source === 'discover') {
          // Move to next profile
          setCurrentIndex(prev => prev + 1);
        } else if (source === 'likes') {
          // Remove from likedBy
          setLikedBy(prev => prev.filter(p => p.userId !== userId));
        } else if (source === 'matches') {
          // Clear selected match if viewing their chat
          if (selectedMatch?.user?.userId === userId) {
            setSelectedMatch(null);
          }
        }
        
        // Remove from matches if they were matched
        setMatches(prev => prev.filter(m => m.user?.userId !== userId));
        
        alert('User blocked successfully');
      } else {
        alert(data.message || 'Failed to block user');
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  // Open report modal
  const openReportModal = (userId, userName, source) => {
    setReportModal({ show: true, userId, userName, source });
    setReportReason('');
    setReportMessage('');
  };

  // Submit report
  const submitReport = async () => {
    if (!reportReason) {
      alert('Please select a reason for your report');
      return;
    }

    setReportSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reportedUserId: reportModal.userId,
          reason: reportReason,
          message: reportMessage,
          source: reportModal.source
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Report submitted successfully. Our team will review it shortly.');
        setReportModal({ show: false, userId: null, userName: '', source: '' });
      } else {
        alert(data.message || 'Failed to submit report');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please try again.');
    } finally {
      setReportSubmitting(false);
    }
  };

  // Archive a match
  const archiveMatch = async (matchId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/matches/${matchId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        // Close the chat if it's the selected match
        if (selectedMatch?.matchId === matchId) {
          setSelectedMatch(null);
        }
        // Reload matches to update the list
        loadMatches(token);
        console.log('✅ Match archived');
      }
    } catch (error) {
      console.error('Archive match error:', error);
    }
  };

  // Unarchive a match (reinitiate contact)
  const unarchiveMatch = async (matchId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/matches/${matchId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        // Reload matches to get the unarchived one
        setShowArchivedMatches(false);
        loadMatches(token);
        console.log('✅ Match unarchived');
      }
    } catch (error) {
      console.error('Unarchive match error:', error);
    }
  };

  // Archive a date booking
  const archiveDateBooking = async (dateId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/dates/${dateId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        // Reload both lists so active removes it and archived gains it
        loadAllDateBookings(token, matches);
        console.log('✅ Date archived');
      }
    } catch (error) {
      console.error('Archive date error:', error);
    }
  };

  // Unarchive a date booking
  const unarchiveDateBooking = async (dateId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/dates/${dateId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        // Reload both lists so archived removes it and active gains it
        loadAllDateBookings(token, matches);
        console.log('✅ Date unarchived');
      }
    } catch (error) {
      console.error('Unarchive date error:', error);
    }
  };

  // Load messages for a match
  const loadMessages = async (matchId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/messages/${matchId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setMessages(prev => ({
          ...prev,
          [matchId]: data.data
        }));
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedMatch) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          matchId: selectedMatch.matchId,
          content: messageInput,
          type: 'text'
        })
      });

      console.log('Message response status:', response.status);
      const data = await response.json();
      console.log('Message response data:', data);

      // Check for content moderation violation
      if (!data.success && data.isViolation) {
        console.log('Moderation violation detected:', data);
        setModerationWarning({
          message: data.warningMessage || data.message,
          strikes: data.strikes,
          maxStrikes: data.maxStrikes || 3,
          isBanned: data.isBanned || false,
          severity: data.severity
        });
        setMessageInput(''); // Clear the offensive message
        return; // Don't send the message
      }

      // Check if user is banned
      if (!data.success && data.isBanned) {
        console.log('User is banned:', data);
        setModerationWarning({
          message: data.message,
          strikes: data.strikes || 3,
          maxStrikes: 3,
          isBanned: true,
          severity: 'high'
        });
        return;
      }

      if (data.success) {
        const newMessage = {
          content: messageInput,
          sender: user.id,
          createdAt: new Date()
        };
        
        setMessages(prev => ({
          ...prev,
          [selectedMatch.matchId]: [...(prev[selectedMatch.matchId] || []), newMessage]
        }));
        
        setMessageInput('');
      } else {
        // Other errors (not moderation)
        console.error('Message send failed:', data);
        setError(data.message || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Network error. Please try again.');
    }
  };

  // Select match and load messages
  const selectMatch = (match) => {
    setSelectedMatch(match);
    loadMessages(match.matchId);
  };

// Accept a date proposal
const handleAcceptDate = async (dateId, selectedDateTime) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${SPARK_URL}/dates/${dateId}/accept`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        status: 'accepted',
        selectedDateTime: selectedDateTime || undefined
      })
    });

    const data = await response.json();
    
    if (data.success) {
      alert('✅ Date accepted!');
      loadAllDateBookings(token, matches);
    } else {
      alert('Failed to accept date: ' + data.message);
    }
  } catch (err) {
    console.error('Error accepting date:', err);
    alert('Failed to accept date');
  }
};

// Confirm venue booking
const handleConfirmBooking = async (dateId, confirmationNote, bookingReference) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${SPARK_URL}/dates/${dateId}/confirm-booking`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        confirmationNote: confirmationNote || 'Booking confirmed',
        bookingReference: bookingReference || ''
      })
    });

    const data = await response.json();
    if (data.success) {
      alert('✅ Booking confirmed! Your date partner has been notified.');
      loadAllDateBookings(token, matches);
    } else {
      alert('Failed to confirm booking: ' + data.message);
    }
  } catch (err) {
    console.error('Error confirming booking:', err);
    alert('Failed to confirm booking');
  }
};

// Decline a date proposal with reason
const handleDeclineDate = async (dateId, reason) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${SPARK_URL}/dates/${dateId}/decline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        reason: reason
      })
    });

    const data = await response.json();
    
    if (data.success) {
      alert('Date declined. They will be notified of your reason.');
      loadAllDateBookings(token, matches);
    } else {
      alert('Failed to decline date: ' + data.message);
    }
  } catch (err) {
    console.error('Error declining date:', err);
    alert('Failed to decline date');
  }
};

// Cancel a date with reason
const handleCancelDate = async (dateId, reason) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${SPARK_URL}/dates/${dateId}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        reason: reason
      })
    });

    const data = await response.json();
    
    if (data.success) {
      alert('✅ Date cancelled. They will be notified.');
      loadAllDateBookings(token, matches);
    } else {
      alert('Failed to cancel date: ' + data.message);
    }
  } catch (err) {
    console.error('Error cancelling date:', err);
    alert('Failed to cancel date');
  }
};

// Suggest changes to a date proposal

// Toggle interest selection
  const toggleInterest = (interest) => {
    setProfileForm(prev => {
      const newInterests = prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest];
      
      console.log('🎯 Interest clicked:', interest);
      console.log('📊 Previous interests:', prev.interests);
      console.log('✅ New interests:', newInterests);
      
      return {
        ...prev,
        interests: newInterests
      };
    });
  };

  // Toggle gender preference (multi-select)
  const toggleGenderPreference = (gender) => {
    setProfileForm(prev => {
      const current = prev.preferences.interestedIn;
      const newInterestedIn = current.includes(gender)
        ? current.filter(g => g !== gender)
        : [...current, gender];
      
      return {
        ...prev,
        preferences: {
          ...prev.preferences,
          interestedIn: newInterestedIn
        }
      };
    });
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`${SPARK_URL}/profiles/photos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // Update profile form with new photo
        setProfileForm(prev => ({
          ...prev,
          photos: [...prev.photos, data.data.photo]
        }));
        
        // Also update the main profile state
        setProfile(prev => ({
          ...prev,
          photos: [...(prev.photos || []), data.data.photo]
        }));

        alert('Photo uploaded successfully! 📸');
      } else {
        setError(data.message || 'Failed to upload photo');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Photo upload error:', err);
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      event.target.value = '';
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      const token = localStorage.getItem('accessToken');
      
      // Log for debugging
      console.log('Deleting photo with ID:', photoId);
      console.log('Current photos:', profileForm.photos);
      
      if (!photoId) {
        setError('Photo ID is missing');
        console.error('Photo object missing _id field');
        return;
      }
      
      const response = await fetch(`${SPARK_URL}/profiles/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Remove photo from profile form - check both id and _id
        setProfileForm(prev => ({
          ...prev,
          photos: prev.photos.filter(p => {
            const pId = p._id || p.id;
            return pId !== photoId;
          })
        }));
        
        // Also update the main profile state
        setProfile(prev => ({
          ...prev,
          photos: (prev.photos || []).filter(p => {
            const pId = p._id || p.id;
            return pId !== photoId;
          })
        }));

        alert('Photo deleted ✓');
      } else {
        setError(data.message || 'Failed to delete photo');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Delete photo error:', err);
    }
  };

  // Save profile

const handleSaveProfile = async () => {
  setLoading(true);
  setError('');

  // Validate minimum 3 photos
  if (profileForm.photos.length < 3) {
    setError('Please upload at least 3 photos to save your profile');
    setLoading(false);
    return;
  }

  try {
    const token = localStorage.getItem('accessToken');
    
    // Use cached location if available (from previous save or background fetch)
    // This prevents blocking the save on a slow geolocation request
    let userLocation = window._cachedSparkLocation || {
      type: 'Point',
      coordinates: [146.8169, -19.2590], // Fallback to Townsville
      city: 'Townsville',
      country: 'Australia'
    };

    // Fire-and-forget: update cached location in the background for NEXT save
    // This way we never block the current save on geolocation
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          console.log('📍 Background location update:', lat, lng);
          
          try {
            const geoResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`,
              { headers: { 'User-Agent': 'SparkDatingApp/1.0' } }
            );
            
            if (geoResponse.ok) {
              const geoData = await geoResponse.json();
              window._cachedSparkLocation = {
                type: 'Point',
                coordinates: [lng, lat],
                city: geoData.address?.city || 
                      geoData.address?.town || 
                      geoData.address?.suburb || 
                      geoData.address?.village || 
                      'Unknown City',
                state: geoData.address?.state || '',
                country: geoData.address?.country || 'Unknown'
              };
              console.log('✅ Cached location for next save:', window._cachedSparkLocation.city);
            }
          } catch (e) {
            console.warn('Background geocode failed:', e);
          }
        },
        () => {}, // silently ignore errors
        { timeout: 5000, maximumAge: 300000, enableHighAccuracy: false }
      );
    }

    // Convert frontend preference names to backend schema
    const backendPreferences = {
      minAge: profileForm.preferences.ageMin,
      maxAge: profileForm.preferences.ageMax,
      maxDistance: profileForm.preferences.distance,
      interestedIn: profileForm.preferences.interestedIn.length > 0 
        ? profileForm.preferences.interestedIn 
        : ['male', 'female', 'non-binary', 'other'],
      showMe: 'everyone'
    };

    console.log('📊 Frontend preferences:', profileForm.preferences);
    console.log('📊 Backend preferences:', backendPreferences);

    // Use the detected or default location
    const profileDataWithLocation = {
      bio: profileForm.bio,
      interests: profileForm.interests,
      occupation: profileForm.occupation,
      education: profileForm.education,
      height: profileForm.height ? parseInt(profileForm.height) : undefined, // Just a number in cm
      preferences: backendPreferences,
      location: userLocation
    };

    // Add survey data if it exists
    if (profileForm.surveyResults) {
      profileDataWithLocation.surveyResults = profileForm.surveyResults;
      profileDataWithLocation.personalityProfile = profileForm.personalityProfile;
    }
    
    // Add MBTI type
    if (profileForm.mbtiType) {
      profileDataWithLocation.mbtiType = profileForm.mbtiType;
    }
    
    // Add lifestyle fields - nested under preferences.lifestyle
    profileDataWithLocation.kids = profileForm.kids || undefined;
    profileDataWithLocation.religion = profileForm.religion || undefined;
    profileDataWithLocation.pets = profileForm.pets || undefined;
    
    // Create or update preferences.lifestyle for smoking/drinking/cannabis
    // IMPORTANT: Use undefined instead of '' for empty values (schema doesn't accept empty strings)
    if (!profileDataWithLocation.preferences) {
      profileDataWithLocation.preferences = {};
    }
    if (!profileDataWithLocation.preferences.lifestyle) {
      profileDataWithLocation.preferences.lifestyle = {};
    }
    profileDataWithLocation.preferences.lifestyle.smoking = profileForm.smoking || undefined;
    profileDataWithLocation.preferences.lifestyle.drinking = profileForm.drinking || undefined;
    profileDataWithLocation.preferences.lifestyle.cannabis = profileForm.marijuana || undefined;  // Note: backend uses 'cannabis' not 'marijuana'

    console.log('📤 Saving profile with location:', userLocation);

    const response = await fetch(`${SPARK_URL}/profiles/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileDataWithLocation)
    });

    const data = await response.json();

    if (data.success) {
  console.log('✅ Profile saved successfully:', data.data);
  console.log('🔍 DEBUG: Checking lifestyle values in response...');
  console.log('  - preferences.lifestyle:', data.data.preferences?.lifestyle);
  console.log('  - preferences.lifestyle.smoking:', data.data.preferences?.lifestyle?.smoking);
  console.log('  - preferences.lifestyle.drinking:', data.data.preferences?.lifestyle?.drinking);
  console.log('  - preferences.lifestyle.cannabis:', data.data.preferences?.lifestyle?.cannabis);
  console.log('  - top-level smoking:', data.data.smoking);
  console.log('  - top-level drinking:', data.data.drinking);
  console.log('  - top-level marijuana:', data.data.marijuana);
  
  // IMPORTANT: Update the profile state with the saved data
  setProfile(data.data);
  
  // Also update the form to show saved data
  const newFormData = {
    bio: data.data.bio || '',
    interests: data.data.interests || [],
    occupation: data.data.occupation || '',
    education: data.data.education || '',
    height: data.data.height?.toString() || '',
    photos: data.data.photos || [],
    preferences: {
      ageMin: data.data.preferences?.minAge || 18,
      ageMax: data.data.preferences?.maxAge || 50,
      distance: data.data.preferences?.maxDistance || 50,
      interestedIn: data.data.preferences?.interestedIn || []
    },
    surveyResults: data.data.surveyResults || surveyData,
    personalityProfile: data.data.personalityProfile || '',
    mbtiType: data.data.mbtiType || '',
    kids: data.data.kids || '',
    religion: data.data.religion || '',
    pets: data.data.pets || '',
    smoking: data.data.preferences?.lifestyle?.smoking || data.data.smoking || '',
    drinking: data.data.preferences?.lifestyle?.drinking || data.data.drinking || '',
    marijuana: data.data.preferences?.lifestyle?.cannabis || data.data.marijuana || ''
  };
  
  console.log('🔍 DEBUG: New form data being set:');
  console.log('  - smoking:', newFormData.smoking);
  console.log('  - drinking:', newFormData.drinking);
  console.log('  - marijuana:', newFormData.marijuana);
  
  setProfileForm(newFormData);
  
  setEditingProfile(false);
      
      // CRITICAL: Reload discovery profiles with new location/preferences
      console.log('🔄 Reloading discovery profiles...');
      await loadDiscoverProfiles(token);
      
      setCurrentView('discover');
      // Non-blocking success — no more alert() popup
      console.log(`✅ Profile saved. Location: ${userLocation.city}, ${userLocation.country}`);
    } else {
      setError(data.message || 'Failed to update profile');
    }
  } catch (err) {
    setError('Network error. Please try again.');
    console.error('Save profile error:', err);
  } finally {
    setLoading(false);
  }
};

  // Handle survey change
  const handleSurveyChange = (field, value) => {
    setSurveyData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate survey results
  const calculateSurveyResults = () => {
    const personality = {
      introvert: surveyData.socialEnergy === 'recharge' ? 1 : 0,
      extrovert: surveyData.socialEnergy === 'energize' ? 1 : 0,
      adventurous: surveyData.weekend === 'adventure' ? 1 : 0,
      homebody: surveyData.weekend === 'relax' ? 1 : 0,
      planner: surveyData.planning === 'plan' ? 1 : 0,
      spontaneous: surveyData.planning === 'spontaneous' ? 1 : 0
    };

    let profile = [];
    if (personality.introvert) profile.push("Introspective");
    if (personality.extrovert) profile.push("Social");
    if (personality.adventurous) profile.push("Adventurous");
    if (personality.homebody) profile.push("Comfort-oriented");
    if (personality.planner) profile.push("Organized");
    if (personality.spontaneous) profile.push("Spontaneous");

    return profile.join(", ");
  };

  // Save survey results
  const handleSaveSurvey = async () => {
    const surveyResults = calculateSurveyResults();
    
    // Add survey results to bio or as separate field
    setProfileForm(prev => ({
      ...prev,
      surveyResults: surveyData,
      personalityProfile: surveyResults
    }));

    setShowSurvey(false);
    alert('Survey completed! ✨ Your personality profile has been added to your profile.');
  };

  
  
  

  // Called by NarbodicaAuth on successful login/register
  const handleAuthSuccess = async (tokens, user) => {
    // Clear previous user state
    setMatches([]); setLikedBy([]); setProfiles([]); setDateBookings([]); setCurrentIndex(0);
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    setIsAuthenticated(true);
        await fetchCurrentUser(tokens.accessToken);
    checkAdminStatus(tokens.accessToken);
    checkSparkLoginImport(tokens.accessToken);
  };

  // If not authenticated, show login/register
  if (!isAuthenticated) {
    // Check if user came from email reset link (has ?token= in URL)
    const urlParams = new URLSearchParams(window.location.search);
    const resetTokenFromUrl = urlParams.get('token');
    const defaultMode = resetTokenFromUrl ? 'reset' : 'login';

    // NarbodicaAuth handles: login, register, MFA, forgot password, reset password, passkeys
    return (
      <>
        <AccessibilityToggle accentColor="#667eea" apiBase={API_URL} />
        <AuthScreen onSuccess={handleAuthSuccess} defaultMode={defaultMode} />
      </>
    );
  }

  // Main app interface
  const sparkAccessToken = localStorage.getItem('accessToken');
  return (
    <div style={{
      minHeight: '100dvh',
      background: theme.vars['--page-bg'],
      fontFamily: '"Inter", -apple-system, sans-serif',
      padding: '20px'
    }}>
      <AccessibilityToggle accentColor="#667eea" token={sparkAccessToken} apiBase={API_URL} />
      <style>{`
        :root {
          --radius-sm: 8px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --radius-xl: 20px;
          --radius-full: 9999px;
          --shadow-sm: 0 1px 2px rgba(0,0,0,0.05);
          --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
          --shadow-lg: 0 12px 32px rgba(0,0,0,0.12);
          --shadow-xl: 0 20px 60px rgba(0,0,0,0.15);
        }

        * { box-sizing: border-box; }
        
        @keyframes slideOut {
          to {
            transform: translateX(${swipeDirection === 'right' ? '150%' : '-150%'}) rotate(${swipeDirection === 'right' ? '20deg' : '-20deg'});
            opacity: 0;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        .swipe-card {
          animation: ${swipeDirection ? 'slideOut 0.3s ease-out forwards' : 'none'};
        }
        
        .fade-in {
          animation: fadeIn 0.35s ease-out;
        }

        .photo-upload-btn:hover {
          background: var(--primary-dark) !important;
        }

        .photo-delete-btn {
          opacity: 0;
          transition: opacity 0.2s;
        }

        .photo-container:hover .photo-delete-btn {
          opacity: 1;
        }

        /* Section card styling */
        .profile-section {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          padding: 20px;
          margin-bottom: 16px;
          transition: border-color 0.2s;
        }
        .profile-section:hover {
          border-color: var(--primary-light);
        }
        .profile-section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 14px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* Custom range slider */
        input[type="range"] {
          -webkit-appearance: none;
          height: 6px;
          border-radius: 3px;
          background: var(--border);
          outline: none;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--primary);
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(99,102,241,0.3);
        }

        /* Scrollbar styling */
        .profile-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .profile-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .profile-scroll::-webkit-scrollbar-thumb {
          background: var(--border);
          border-radius: 2px;
        }

        /* Nav tab styling */
        .nav-tab {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 8px 4px;
          border: none;
          border-radius: var(--radius-md);
          cursor: pointer;
          font-weight: 600;
          font-size: 10px;
          transition: all 0.2s;
          min-width: 0;
          flex: 1;
        }
        .nav-tab-active {
          background: var(--nav-active-bg);
          color: var(--nav-active-text);
          box-shadow: var(--shadow-sm);
        }
        .nav-tab-inactive {
          background: transparent;
          color: var(--nav-inactive-text);
        }
        .nav-tab-inactive:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }

        /* Input focus styles */
        .spark-input:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1) !important;
        }

        /* Badge pulse */
        .badge-pulse {
          animation: pulse-soft 2s ease-in-out infinite;
        }

        /* ============================================ */
        /* MINIMAL SCROLLBAR STYLES - Blended & Elegant */
        /* ============================================ */
        
        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
        }
        
        /* Webkit browsers (Chrome, Safari, Edge) */
        *::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        *::-webkit-scrollbar-track {
          background: transparent;
        }
        
        *::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.15);
          border-radius: 10px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        *::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.25);
        }
        
        /* For light theme - adjust scrollbar color */
        [data-theme="light"] * {
          scrollbar-color: rgba(0, 0, 0, 0.15) transparent;
        }
        
        [data-theme="light"] *::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.05);
        }
        
        [data-theme="light"] *::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.25);
        }
        
        /* Hide scrollbar when not hovering (optional - ultra minimal) */
        *:not(:hover)::-webkit-scrollbar-thumb {
          background: transparent;
        }
      `}</style>

      {/* ===== GEO CHECK ===== */}
      {geoStatus === 'checking' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          color: 'white', textAlign: 'center', padding: '24px'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✨</div>
          <h1 style={{ fontSize: '32px', fontWeight: 800, margin: '0 0 8px' }}>Spark</h1>
          <p style={{ fontSize: '16px', opacity: 0.8 }}>Loading…</p>
        </div>
      )}

      {geoStatus === 'blocked' && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          color: 'white', textAlign: 'center', padding: '32px'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '24px' }}>🌏</div>
          <h1 style={{ fontSize: '36px', fontWeight: 800, margin: '0 0 12px' }}>Spark</h1>
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            WebkitBackdropFilter: 'blur(10px)',
            backdropFilter: 'blur(10px)',
            borderRadius: '20px',
            padding: '32px 28px',
            maxWidth: '420px',
            width: '100%'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🇦🇺</div>
            <h2 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 12px' }}>
              Australia Only — For Now
            </h2>
            <p style={{ fontSize: '15px', opacity: 0.9, lineHeight: 1.6, margin: '0 0 20px' }}>
              Spark is currently available in Australia only. We're working hard to expand to more countries soon!
            </p>
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '13px',
              opacity: 0.85,
              lineHeight: 1.5
            }}>
              🔔 Want to be notified when we launch in your country?
              <br />
              <a href="mailto:hello@narbodica.com.au" style={{ color: 'white', fontWeight: 600 }}>
                hello@narbodica.com.au
              </a>
            </div>
          </div>
          <p style={{ marginTop: '24px', fontSize: '13px', opacity: 0.6 }}>
            © {new Date().getFullYear()} Narbodica · Spark
          </p>
        </div>
      )}

      {/* ===== REST OF APP (geo screens are fixed overlays above) ===== */}

      {/* Verification Screen - Shows if user needs verification — NO SKIP */}
      {isAuthenticated && needsVerification && (
        <VerifyIdentity 
          onVerified={handleVerificationComplete}
        />
      )}

      {/* Onboarding Shell - universal stepper for new users */}
      {isAuthenticated && !needsVerification && needsOnboarding && (
        <OnboardingShell
          app="spark"
          appLabel="Spark"
          accentColor="#8b5cf6"
          token={localStorage.getItem('accessToken')}
          user={user}
          apiBase={API_BASE}
          steps={getSparkOnboardingSteps()}
          onComplete={handleOnboardingComplete}
        />
      )}

      {/* Cross-App Import — returning user with empty profile fields */}
      {isAuthenticated && !needsVerification && !needsOnboarding && showLoginImport && loginImportData && (
        <SparkLoginImport
          imports={loginImportData}
          onComplete={() => {
            setShowLoginImport(false);
            const token = localStorage.getItem('accessToken');
            if (token) fetchCurrentUser(token, true);
          }}
          onLogout={handleLogout}
        />
      )}

      {/* Subscription Paywall - Show if subscription expired/invalid */}
      {isAuthenticated && !needsVerification && !needsOnboarding && !showLoginImport && subscription && !subscription.isValid && !skippedPaywall && (
        <div>
          <SubscriptionPaywall
            subscription={subscription}
            user={user}
            onSubscriptionUpdate={(sub) => setSubscription(sub)}
          />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 20px 28px', background: 'linear-gradient(transparent, #09090b 40%)', zIndex: 1001, textAlign: 'center' }}>
            <button onClick={() => setSkippedPaywall(true)} style={{ background: 'none', border: 'none', color: '#71717a', fontSize: 14, cursor: 'pointer', fontFamily: '"Inter", sans-serif', padding: '10px 20px' }}>
              Subscribe later
            </button>
          </div>
        </div>
      )}

      {/* Subscription Management - When user clicks to manage subscription */}
      {showSubscriptionManage && subscription && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }}>
          <SubscriptionPaywall
            subscription={subscription}
            user={user}
            onSubscriptionUpdate={(sub) => setSubscription(sub)}
            onClose={() => setShowSubscriptionManage(false)}
          />
        </div>
      )}

      {/* Main App Content - Only show if not needing verification, onboarding, AND has valid subscription or skipped paywall */}
      {isAuthenticated && !needsVerification && !needsOnboarding && !showLoginImport && (!subscription || subscription.isValid || skippedPaywall) && (
      <div style={{
        maxWidth: '440px',
        margin: '0 auto',
        background: 'var(--card-bg)',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: 'var(--card-shadow)',
        minHeight: '90vh'
      }}>
        {/* Header */}
        <div style={{
          background: theme.vars['--header-bg'],
          padding: '20px 20px 16px',
          color: 'white'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '16px'
          }}>
            <Flame size={26} strokeWidth={2.5} />
            <h1 style={{
              margin: 0,
              fontSize: '24px',
              fontFamily: '"DM Serif Display", serif',
              fontWeight: 400,
              letterSpacing: '-0.3px'
            }}>Spark</h1>
            <div style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '12px', opacity: 0.9, fontWeight: 500 }}>
                {profile?.firstName}
              </span>
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  background: showSettings ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '6px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s'
                }}
                title="Settings"
              >
                <Settings size={15} />
              </button>
              {isAdmin && (
                <button
                  onClick={() => window.open('/admin.html', '_blank')}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    padding: '6px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s'
                  }}
                  title="Admin"
                >
                  <Shield size={15} />
                </button>
              )}
              <button
                onClick={handleLogout}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '6px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s'
                }}
              >
                <LogOut size={15} />
              </button>
            </div>
          </div>
          
          {/* Trial banner */}
          {subscription?.isTrialActive && (
            <div 
              onClick={() => setShowSubscriptionManage(true)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                padding: '6px 12px', marginBottom: '8px',
                background: 'rgba(245,158,11,0.2)', borderRadius: '8px',
                fontSize: '12px', color: '#fbbf24', cursor: 'pointer',
                border: '1px solid rgba(245,158,11,0.3)',
                transition: 'all 0.2s'
              }}
            >
              <span>⏳</span>
              <span style={{ fontWeight: 600 }}>Free Trial</span>
              <span style={{ opacity: 0.8 }}>— {subscription.daysRemaining} day{subscription.daysRemaining !== 1 ? 's' : ''} left</span>
              <span style={{ marginLeft: '4px', opacity: 0.6 }}>›</span>
            </div>
          )}

          <div style={{
  display: 'flex',
  gap: '4px',
  background: 'var(--nav-bar-bg)',
  borderRadius: 'var(--radius-md)',
  padding: '3px'
}}>
  {['discover', 'likes', 'matches', 'dates', 'profile'].map(view => (
    <button
      key={view}
      className={`nav-tab ${currentView === view ? 'nav-tab-active' : 'nav-tab-inactive'}`}
      onClick={() => {
  setShowSettings(false);
  setCurrentView(view);
  // Reset explore input panel when leaving discover tab
  if (view !== 'discover') {
    setLocationMode('auto');
    setExploreInput('');
    setLocationError('');
    setExploreSuggestions([]);
    setShowSuggestions(false);
  }
  if (view === 'profile') {
    if (profile && currentView !== 'profile') {
      setProfileForm({
        bio: profile.bio || '',
        interests: profile.interests || [],
        occupation: profile.occupation || '',
        education: profile.education || '',
        height: profile.height?.toString() || '',
        photos: profile.photos || [],
        preferences: {
          ageMin: profile.preferences?.minAge || 18,
          ageMax: profile.preferences?.maxAge || 50,
          distance: profile.preferences?.maxDistance || 50,
          interestedIn: profile.preferences?.interestedIn || []
        },
        surveyResults: profile.surveyResults || surveyData,
        personalityProfile: profile.personalityProfile || '',
        mbtiType: profile.mbtiType || '',
        kids: profile.kids || '',
        religion: profile.religion || '',
        pets: profile.pets || '',
        smoking: profile.preferences?.lifestyle?.smoking || profile.smoking || '',
        drinking: profile.preferences?.lifestyle?.drinking || profile.drinking || '',
        marijuana: profile.preferences?.lifestyle?.cannabis || profile.marijuana || ''
      });
      
      // Also populate surveyData if exists
      if (profile.surveyResults) {
        setSurveyData({
          socialEnergy: profile.surveyResults.socialEnergy || '',
          weekend: profile.surveyResults.weekend || '',
          conflict: profile.surveyResults.conflict || '',
          planning: profile.surveyResults.planning || '',
          values: profile.surveyResults.values || '',
          communication: profile.surveyResults.communication || '',
          interests: profile.surveyResults.interests || '',
          lifestyle: profile.surveyResults.lifestyle || ''
        });
      }
    }
    setEditingProfile(true);
  }
}}
    >
      {view === 'discover' && <Sparkles size={16} />}
      {view === 'likes' && <Heart size={16} />}
      {view === 'matches' && <MessageCircle size={16} />}
      {view === 'dates' && <span style={{ fontSize: '15px', lineHeight: 1 }}>📅</span>}
      {view === 'profile' && <User size={16} />}
      
      <span style={{ fontSize: '10px', whiteSpace: 'nowrap', fontWeight: 600 }}>
        {view === 'discover' ? 'Find' : view.charAt(0).toUpperCase() + view.slice(1)}
      </span>
      
      {/* Likes badge */}
      {view === 'likes' && likedBy.length > 0 && (
        <span className="badge-pulse" style={{
          position: 'absolute',
          top: '1px',
          right: '1px',
          background: 'var(--accent)',
          color: 'white',
          borderRadius: 'var(--radius-full)',
          padding: '1px 5px',
          fontSize: '9px',
          fontWeight: 700,
          minWidth: '16px',
          height: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid var(--primary)'
        }}>
          {likedBy.length}
        </span>
      )}
      
      {/* Dates badge */}
      {view === 'dates' && dateBookings.filter(b => 
        b.status === 'pending' && b.proposedTo === user.id
      ).length > 0 && (
        <span className="badge-pulse" style={{
          position: 'absolute',
          top: '1px',
          right: '1px',
          background: 'var(--accent)',
          color: 'white',
          borderRadius: 'var(--radius-full)',
          padding: '1px 5px',
          fontSize: '9px',
          fontWeight: 700,
          minWidth: '16px',
          height: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid var(--primary)'
        }}>
          {dateBookings.filter(b => 
            b.status === 'pending' && b.proposedTo === user.id
          ).length}
        </span>
      )}
    </button>
  ))}
</div>

          {/* Admin button moved to header */}
        </div>

        {/* ═══════ SETTINGS PAGE ═══════ */}

        {/* ═══════ SETTINGS VIEW ═══════ */}
        {showSettings && (
          <SettingsView
            onClose={() => setShowSettings(false)}
            profile={profile} user={user} matches={matches} messages={messages}
            subscription={subscription}
            expandedSettings={expandedSettings} setExpandedSettings={setExpandedSettings}
            showSubscriptionManage={showSubscriptionManage} setShowSubscriptionManage={setShowSubscriptionManage}
            onLogout={handleLogout}
            onNeedsVerification={() => setNeedsVerification(true)}
          />
        )}

        {/* ═══════ MAIN CONTENT ═══════ */}
        {!showSettings && <>

        {currentView === 'profile' && editingProfile && (
          <ProfileView
            profile={profile} user={user} matches={matches}
            loading={loading} error={error} uploadingPhoto={uploadingPhoto}
            editingProfile={editingProfile} setEditingProfile={setEditingProfile}
            expandedSections={expandedSections} setExpandedSections={setExpandedSections}
            profileForm={profileForm} setProfileForm={setProfileForm}
            surveyData={surveyData} showSurvey={showSurvey} setShowSurvey={setShowSurvey}
            interestSearch={interestSearch} setInterestSearch={setInterestSearch}
            showInterestModal={showInterestModal} setShowInterestModal={setShowInterestModal}
            activeInterestTab={activeInterestTab} setActiveInterestTab={setActiveInterestTab}
            onSaveProfile={handleSaveProfile} onDeletePhoto={handleDeletePhoto}
            onPhotoUpload={handlePhotoUpload} onSaveSurvey={handleSaveSurvey}
            onSurveyChange={handleSurveyChange}
            toggleInterest={toggleInterest} toggleGenderPreference={toggleGenderPreference}
            setCurrentView={setCurrentView}
            onNeedsVerification={() => setNeedsVerification(true)}
          />
        )}

        {currentView === 'discover' && (<>
          {/* Discovery mode compact selector */}
          <DiscoveryModeSelector currentMode={discoveryMode} onSelect={changeDiscoveryMode} compact />

          {/* Passed list link */}
          {!showPassedList && discoveryMode !== 'maia' && (
            <div style={{ textAlign: 'right', padding: '0 16px 8px' }}>
              <button onClick={() => setShowPassedList(true)} style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                fontSize: 12, cursor: 'pointer', textDecoration: 'underline',
              }}>Review passed profiles</button>
            </div>
          )}

          {/* Passed list view */}
          {showPassedList && (
            <PassedList onBack={() => setShowPassedList(false)} />
          )}

          {/* Browse mode */}
          {!showPassedList && discoveryMode === 'browse' && (
            <BrowseMode
              profiles={profiles} currentIndex={currentIndex} user={user}
              onSwipe={(dir, idx) => { handlePassProfile(idx); if (dir === 'right') handleSwipe('right'); }}
              onMaybe={handleMaybeProfile}
            />
          )}

          {/* Blind mode */}
          {!showPassedList && discoveryMode === 'blind' && (
            <BlindMode
              profiles={profiles} currentIndex={currentIndex} user={user}
              onSwipe={(dir, idx) => { if (dir === 'left') handlePassProfile(idx); else handleSwipe('right'); }}
              onMaybe={handleMaybeProfile}
            />
          )}

          {/* Maia introduces mode */}
          {!showPassedList && discoveryMode === 'maia' && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>💜</span>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px' }}>
                Maia introduces
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 320, margin: '0 auto 20px' }}>
                No swiping needed. Maia will introduce you to someone when she's found the right person. Open Maia and tell her what you're looking for.
              </p>
              <a href="https://maia.narbodica.com.au" target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-block', padding: '12px 24px', borderRadius: 12,
                background: 'var(--primary)', color: '#fff', textDecoration: 'none',
                fontSize: 14, fontWeight: 600,
              }}>Open Maia</a>
            </div>
          )}

          {/* Stack mode (existing) + Scroll mode (same view for now) */}
          {!showPassedList && (discoveryMode === 'stack' || discoveryMode === 'scroll') && (
          <DiscoverView
            profile={profile} profiles={profiles} currentIndex={currentIndex}
            user={user} loading={loading} matches={matches}
            discoverPhotoIndex={discoverPhotoIndex} setDiscoverPhotoIndex={setDiscoverPhotoIndex}
            expandedScores={expandedScores} setExpandedScores={setExpandedScores}
            expandedSections={expandedSections} setExpandedSections={setExpandedSections}
            profileForm={profileForm} setEditingProfile={setEditingProfile}
            swipeDirection={swipeDirection} touchDelta={touchDelta} lastSwipe={lastSwipe}
            locationMode={locationMode} setLocationMode={setLocationMode}
            postcodeInput={postcodeInput} setPostcodeInput={setPostcodeInput}
            exploreInput={exploreInput} setExploreInput={setExploreInput}
            exploreSuggestions={exploreSuggestions} setExploreSuggestions={setExploreSuggestions}
            showSuggestions={showSuggestions} setShowSuggestions={setShowSuggestions}
            exploreLocation={exploreLocation} setExploreLocation={setExploreLocation}
            locationLoading={locationLoading} locationError={locationError} setLocationError={setLocationError}
            densityData={densityData} densityLoading={densityLoading}
            exploreMapRef={exploreMapRef} exploreMapInstance={exploreMapInstance}
            locationFailed={locationFailed} isSwipingRef={isSwipingRef}
            onSwipe={handleSwipe} onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}
            onUndoLastSwipe={undoLastSwipe} onBlockUser={blockUser} onOpenReportModal={openReportModal}
            onPostcodeLookup={handlePostcodeLookup} onExploreInputChange={handleExploreInputChange}
            onExploreLookup={handleExploreLookup} onSelectSuggestion={handleSelectSuggestion}
            onResetExplore={handleResetExplore} onFetchDensityData={fetchDensityData}
            setCurrentView={setCurrentView}
            onNeedsVerification={() => setNeedsVerification(true)}
          />
          )}
        </>)}

        {currentView === 'likes' && (
          <LikesView
            likedBy={likedBy} profile={profile} user={user}
            onLikeBack={handleLikeBack} onBlockUser={blockUser} onOpenReportModal={openReportModal}
          />
        )}

        {currentView === 'matches' && (
          <MatchesView
            matches={matches} archivedMatches={archivedMatches}
            showArchivedMatches={showArchivedMatches} setShowArchivedMatches={setShowArchivedMatches}
            selectedMatch={selectedMatch} setSelectedMatch={setSelectedMatch}
            messages={messages} messageInput={messageInput} setMessageInput={setMessageInput}
            user={user} onSendMessage={sendMessage}
            onSelectMatch={selectMatch} onArchiveMatch={archiveMatch} onUnarchiveMatch={unarchiveMatch}
            onBlockUser={blockUser} onOpenReportModal={openReportModal}
            setShowDateModal={setShowDateModal}
            onNeedsVerification={() => setNeedsVerification(true)}
          />
        )}

        {currentView === 'dates' && (
          <DatePlannerView
            dateBookings={dateBookings} archivedDateBookings={archivedDateBookings}
            showArchivedDates={showArchivedDates} setShowArchivedDates={setShowArchivedDates}
            matches={matches} profile={profile} user={user} error={error}
            onAcceptDate={handleAcceptDate} onDeclineDate={handleDeclineDate}
            onCancelDate={handleCancelDate} onConfirmBooking={handleConfirmBooking}
            onArchiveDateBooking={archiveDateBooking} onUnarchiveDateBooking={unarchiveDateBooking}
            onLoadAllDateBookings={loadAllDateBookings}
            setSelectedMatch={setSelectedMatch} setShowDateModal={setShowDateModal}
            setCounterProposalBooking={setCounterProposalBooking}
            DatesViewComponent={
              <DatesView
                dateBookings={dateBookings} archivedDateBookings={archivedDateBookings}
                showArchivedDates={showArchivedDates} setShowArchivedDates={setShowArchivedDates}
                matches={matches} profile={profile} user={user} error={error}
                onAcceptDate={handleAcceptDate} onDeclineDate={handleDeclineDate}
                onCancelDate={handleCancelDate} onConfirmBooking={handleConfirmBooking}
                onArchiveDateBooking={archiveDateBooking} onUnarchiveDateBooking={unarchiveDateBooking}
                onLoadAllDateBookings={loadAllDateBookings}
                setSelectedMatch={setSelectedMatch} setShowDateModal={setShowDateModal}
                setCounterProposalBooking={setCounterProposalBooking}
              />
            }
          />
        )}

        </>}
        {/* Moderation Warning Modal */}
        {moderationWarning && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              {/* Warning Icon */}
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: moderationWarning.isBanned ? '#ff4444' : '#ff9500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                {moderationWarning.isBanned ? '🚫' : '⚠️'}
              </div>

              {/* Title */}
              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#2c3e50',
                textAlign: 'center',
                marginBottom: '16px'
              }}>
                {moderationWarning.isBanned ? 'Account Banned' : 'Community Guidelines Violation'}
              </h2>

              {/* Message */}
              <p style={{
                fontSize: '16px',
                color: '#666',
                textAlign: 'center',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                {moderationWarning.message}
              </p>

              {/* Strike Counter (if not banned) */}
              {!moderationWarning.isBanned && (
                <div style={{
                  background: '#fff3e0',
                  padding: '16px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#f57c00',
                    fontWeight: 600,
                    marginBottom: '8px'
                  }}>
                    Strike {moderationWarning.strikes} of {moderationWarning.maxStrikes}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'center'
                  }}>
                    {[...Array(moderationWarning.maxStrikes)].map((_, i) => (
                      <div key={i} style={{
                        width: '40px',
                        height: '8px',
                        borderRadius: '4px',
                        background: i < moderationWarning.strikes ? '#f57c00' : '#e0e0e0'
                      }} />
                    ))}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#666',
                    marginTop: '12px'
                  }}>
                    {moderationWarning.strikes >= 2 
                      ? '⚠️ One more strike and your account will be permanently banned'
                      : `${moderationWarning.maxStrikes - moderationWarning.strikes} strikes remaining`}
                  </div>
                </div>
              )}

              {/* Ban Message */}
              {moderationWarning.isBanned && (
                <div style={{
                  background: '#ffebee',
                  padding: '16px',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: '#c62828',
                    fontWeight: 600
                  }}>
                    Your account has been permanently banned for repeated violations of our community guidelines.
                  </div>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => {
                  setModerationWarning(null);
                  if (moderationWarning.isBanned) {
                    // Log out banned user
                    localStorage.clear();
                    window.location.reload();
                  }
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: moderationWarning.isBanned ? '#ff4444' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {moderationWarning.isBanned ? 'I Understand' : 'I Understand - Continue'}
              </button>
            </div>
          </div>
        )}

        {/* Report User Modal */}
        {reportModal.show && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '420px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: '#fef3c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Flag size={22} color="#e67e22" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#2c3e50' }}>
                    Report {reportModal.userName}
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#7f8c8d' }}>
                    Help us keep Spark safe
                  </p>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#2c3e50', marginBottom: '10px' }}>
                  Reason for report *
                </label>
                {[
                  { value: 'fake_profile', label: '🎭 Fake or misleading profile' },
                  { value: 'inappropriate_content', label: '🚫 Inappropriate content or photos' },
                  { value: 'harassment', label: '😡 Harassment or abusive behavior' },
                  { value: 'scam', label: '💰 Scam or spam' },
                  { value: 'underage', label: '🔞 Appears to be underage' },
                  { value: 'threatening', label: '⚠️ Threatening or dangerous behavior' },
                  { value: 'other', label: '📝 Other' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setReportReason(option.value)}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '12px 16px',
                      marginBottom: '8px',
                      borderRadius: '10px',
                      border: reportReason === option.value ? '2px solid #e67e22' : '2px solid #e9ecef',
                      background: reportReason === option.value ? '#fef3c7' : 'white',
                      color: '#2c3e50',
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontWeight: reportReason === option.value ? 600 : 400,
                      transition: 'all 0.2s'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#2c3e50', marginBottom: '8px' }}>
                  Additional details (optional)
                </label>
                <textarea
                  value={reportMessage}
                  onChange={(e) => setReportMessage(e.target.value)}
                  placeholder="Tell us more about what happened..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '2px solid #e9ecef',
                    fontSize: '14px',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setReportModal({ show: false, userId: null, userName: '', source: '' })}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: '#f1f5f9',
                    color: '#64748b',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReport}
                  disabled={!reportReason || reportSubmitting}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: reportReason ? 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)' : '#cbd5e0',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: reportReason ? 'pointer' : 'not-allowed'
                  }}
                >
                  {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Date Booking Modal */}
        {/* Location Update Modal */}
        {locationUpdatePrompt.show && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              background: theme === 'dark' ? '#1f2937' : 'white',
              borderRadius: '16px',
              padding: '28px',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ 
                fontSize: '40px', 
                marginBottom: '16px',
                textAlign: 'center'
              }}>📍</div>
              
              <h3 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '20px',
                fontWeight: '700',
                textAlign: 'center',
                color: theme === 'dark' ? '#f3f4f6' : '#1f2937'
              }}>
                Location Update
              </h3>
              
              <p style={{ 
                margin: '0 0 20px 0', 
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                fontSize: '15px',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                You're in <strong style={{ color: theme === 'dark' ? '#f3f4f6' : '#1f2937' }}>{locationUpdatePrompt.newCity}</strong>
                <br />
                Your profile shows <strong style={{ color: theme === 'dark' ? '#f3f4f6' : '#1f2937' }}>{locationUpdatePrompt.currentCity}</strong>
                <br />
                <span style={{ fontSize: '12px', color: theme === 'dark' ? '#6b7280' : '#9ca3af' }}>
                  ({locationUpdatePrompt.distance} km away)
                </span>
              </p>
              
              <p style={{ 
                margin: '0 0 24px 0', 
                fontSize: '14px',
                textAlign: 'center',
                color: theme === 'dark' ? '#d1d5db' : '#374151'
              }}>
                Update your location to see locals nearby?
              </p>
              
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => {
                    setLocationUpdatePrompt({ show: false, currentCity: '', newCity: '', newCoordinates: [], distance: 0 });
                    // Check again in 24 hours
                    localStorage.setItem('lastLocationCheck', (Date.now() + 82800000).toString());
                  }}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: theme === 'dark' ? '#374151' : '#e5e7eb',
                    color: theme === 'dark' ? '#f3f4f6' : '#1f2937',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  Not Now
                </button>
                <button
                  onClick={async () => {
                    setLoading(true);
                    try {
                      // Get city name via reverse geocoding
                      const [lng, lat] = locationUpdatePrompt.newCoordinates;
                      const geoResponse = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`,
                        { headers: { 'User-Agent': 'SparkDatingApp/1.0' } }
                      );
                      const geoData = await geoResponse.json();
                      
                      // Update profile location
                      const response = await fetch(`${SPARK_URL}/profiles/me`, {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          location: {
                            type: 'Point',
                            coordinates: locationUpdatePrompt.newCoordinates,
                            city: geoData.address?.city || geoData.address?.town || locationUpdatePrompt.newCity,
                            state: geoData.address?.state || '',
                            country: geoData.address?.country || 'Australia'
                          }
                        })
                      });
                      
                      if (response.ok) {
                        // Reload profile and discover profiles
                        await fetchCurrentUser(localStorage.getItem('accessToken'), true);
                        await loadDiscoverProfiles();
                        setLocationUpdatePrompt({ show: false, currentCity: '', newCity: '', newCoordinates: [], distance: 0 });
                        console.log('✅ Location updated successfully');
                      } else {
                        setError('Failed to update location');
                      }
                    } catch (err) {
                      console.error('Error updating location:', err);
                      setError('Failed to update location');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '14px',
                    background: loading ? '#9ca3af' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '15px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.background = '#5568d3';
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.target.style.background = '#667eea';
                  }}
                >
                  {loading ? 'Updating...' : 'Update Location'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showDateModal && selectedMatch && (
          <DateBookingModal
            match={{
              matchId: selectedMatch.matchId,
              otherUserName: selectedMatch.user.firstName
            }}
            counterProposal={counterProposalBooking}
            onClose={() => { setShowDateModal(false); setCounterProposalBooking(null); }}
            onDateProposed={() => {
              const token = localStorage.getItem('accessToken');
              loadAllDateBookings(token);
              setCounterProposalBooking(null);
            }}
          />
        )}
      </div>
      )}

      {/* Feedback */}
      {isAuthenticated && <FeedbackButton app="spark" />}
    </div>
  );
}

// Forgot Password Screen Component

// Reset Password Screen Component

// ============================================
// SPARK LOGIN-TIME CROSS-APP IMPORT
// ============================================
function SparkLoginImport({ imports, onComplete, onLogout }) {
  const [selectedApp, setSelectedApp] = useState(imports?.length === 1 ? imports[0] : null);
  const [selectedFields, setSelectedFields] = useState(() => {
    if (imports?.length === 1) {
      const d = {};
      imports[0].fields.forEach(f => { d[f.key] = true; });
      return d;
    }
    return {};
  });
  const [importing, setImporting] = useState(false);
  useTheme();

  const appBranding = {
    spark:   { name: 'Spark',   icon: '💜', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    connect: { name: 'Connect', icon: '🤝', gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' },
    eros:    { name: 'Eros',    icon: '💕', gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)' },
    artemis: { name: 'Artemis', icon: '🏹', gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' },
  };
  const fieldEmojis = { bio: '📝', photos: '📸', interests: '🏷️', location: '📍', occupation: '💼', education: '🎓', height: '📏', religion: '🙏', smoking: '🚬', drinking: '🍷', mbtiType: '🧠', pets: '🐾', children: '👶' };
  const accent = '#667eea';

  const handleSelectApp = (imp) => {
    setSelectedApp(imp);
    const d = {};
    imp.fields.forEach(f => { d[f.key] = true; });
    setSelectedFields(d);
  };

  const toggleField = (key) => setSelectedFields(p => ({ ...p, [key]: !p[key] }));
  const selectedCount = Object.values(selectedFields).filter(Boolean).length;

  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const res = await fetch(`${API_URL}${url}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...options.headers }
    });
    return res.json();
  };

  const handleImport = async () => {
    if (!selectedApp || selectedCount === 0) { onComplete(); return; }
    setImporting(true);
    try {
      const fieldsToImport = Object.entries(selectedFields).filter(([_, v]) => v).map(([k]) => k);
      const data = await authFetch('/cross-app/import', {
        method: 'POST',
        body: JSON.stringify({ sourceApp: selectedApp.app, fields: fieldsToImport })
      });
      if (data.success && data.data?.data) {
        const d = data.data.data;
        const updates = {};
        if (d.bio) updates.bio = d.bio;
        if (d.interests) updates.interests = d.interests;
        if (d.location) updates.location = d.location;
        if (d.occupation) updates.occupation = d.occupation;
        if (d.education) updates.education = d.education;
        if (d.photos) updates.photos = d.photos;
        if (Object.keys(updates).length > 0) {
          await authFetch('/spark/profiles/me', { method: 'PUT', body: JSON.stringify(updates) });
        }
      }
    } catch (e) { console.error('Login import error:', e); }
    finally { setImporting(false); onComplete(); }
  };

  const handleDismiss = async () => {
    try { await authFetch('/cross-app/dismiss', { method: 'POST', body: JSON.stringify({ currentApp: 'spark' }) }); }
    catch (e) { console.log('Dismiss failed:', e); }
    onComplete();
  };

  const previewText = (field) => {
    if (field.type === 'tags' && Array.isArray(field.preview)) return field.preview.slice(0, 4).join(', ') + (field.preview.length > 4 ? ` +${field.preview.length - 4}` : '');
    if (field.type === 'photos' && Array.isArray(field.preview)) return `${field.preview.length} photo${field.preview.length !== 1 ? 's' : ''}`;
    if (field.type === 'location' && typeof field.preview === 'object') return field.preview.display;
    if (typeof field.preview === 'string') return field.preview.length > 50 ? field.preview.substring(0, 50) + '...' : field.preview;
    return '';
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: '"Inter", -apple-system, sans-serif' }}>
      <div style={{ maxWidth: 520, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Heart size={32} color={accent} style={{ marginBottom: 8 }} />
          <h1 style={{ margin: 0, fontSize: 28, fontFamily: '"DM Serif Display", serif', color: 'var(--text-primary)' }}>Spark</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '4px 0 0' }}>by Narbodica</p>
        </div>

        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 20, padding: 24 }}>
          {!selectedApp && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔗</div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>Complete your profile</h2>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Your Spark profile has some empty fields. Import from your other Narbodica apps?</p>
              </div>
              <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                {imports.map(imp => {
                  const brand = appBranding[imp.app] || {};
                  return (
                    <button key={imp.app} onClick={() => handleSelectApp(imp)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, borderRadius: 14, cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', background: 'var(--hover-bg)', border: '2px solid var(--border)' }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: brand.gradient || accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{imp.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{imp.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{imp.fields.length} field{imp.fields.length !== 1 ? 's' : ''} to import</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <button onClick={onComplete} style={{ width: '100%', padding: 14, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--hover-bg)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 8, fontFamily: '"Inter", sans-serif' }}>Not now</button>
              <button onClick={handleDismiss} style={{ width: '100%', padding: 10, background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: '"Inter", sans-serif' }}>Don't show this again</button>
            </>
          )}

          {selectedApp && (
            <>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, fontSize: 22, background: appBranding[selectedApp.app]?.gradient || accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{selectedApp.icon}</div>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 18 }}>→</span>
                  <div style={{ width: 44, height: 44, borderRadius: 12, fontSize: 22, background: appBranding.spark.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>💜</div>
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Import from {selectedApp.label}</h2>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Fill in your empty Spark fields</p>
              </div>

              {imports.length > 1 && (
                <button onClick={() => setSelectedApp(null)} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 12, padding: 0, fontFamily: '"Inter", sans-serif' }}>← Other apps</button>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 10 }}>
                <button onClick={() => { const a = {}; selectedApp.fields.forEach(f => { a[f.key] = true; }); setSelectedFields(a); }} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: '"Inter", sans-serif' }}>Select all</button>
                <button onClick={() => setSelectedFields({})} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: '"Inter", sans-serif' }}>Deselect all</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                {selectedApp.fields.map(field => {
                  const isOn = selectedFields[field.key];
                  return (
                    <button key={field.key} onClick={() => toggleField(field.key)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', background: isOn ? `${accent}18` : 'var(--hover-bg)', border: isOn ? `2px solid ${accent}` : '1.5px solid var(--border)' }}>
                      <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, background: isOn ? accent : 'transparent', border: isOn ? `2px solid ${accent}` : '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isOn && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{fieldEmojis[field.key] || '📋'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{field.label}</div>
                        {previewText(field) && <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewText(field)}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>

              <button onClick={handleImport} disabled={importing || selectedCount === 0} style={{ width: '100%', padding: 14, borderRadius: 12, border: 'none', background: selectedCount > 0 ? accent : 'var(--border)', color: selectedCount > 0 ? '#fff' : 'var(--text-secondary)', fontSize: 15, fontWeight: 700, cursor: selectedCount > 0 ? 'pointer' : 'not-allowed', fontFamily: '"Inter", sans-serif', marginBottom: 8 }}>
                {importing ? 'Importing...' : `Import ${selectedCount} field${selectedCount !== 1 ? 's' : ''}`}
              </button>
              <button onClick={onComplete} style={{ width: '100%', padding: 14, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--hover-bg)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 8, fontFamily: '"Inter", sans-serif' }}>Not now</button>
              <button onClick={handleDismiss} style={{ width: '100%', padding: 10, background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', fontFamily: '"Inter", sans-serif' }}>Don't show this again</button>
            </>
          )}
        </div>

        <button onClick={onLogout} style={{ display: 'block', margin: '16px auto', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>Sign out</button>
      </div>
    </div>
  );
}